import { HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, finalize, forkJoin, map, Observable, of, shareReplay, switchMap } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { CourierService } from "../../services/courier/courier.service";
import { Order } from "../../services/generated/erp-api/models/order";
import { SalePointDto } from "../../services/generated/erp-api/models/sale-point-dto";
import { SalePointMenuDto } from "../../services/generated/erp-api/models/sale-point-menu-dto";
import { OrderApiService } from "../../services/generated/erp-api/services/order-api.service";
import { SalePointApiService } from "../../services/generated/erp-api/services/sale-point-api.service";
import { CompanyApiService } from "../../services/generated/waba-api/services/company-api.service";
import { SKIP_ERROR_ALERT } from "../../shared/interceptors/skip-error-alert.context";
import { MapsLinkService } from "../../shared/services/maps-link.service";
import { isApiResponseOk, mainSalePointOf, sortSalePoints } from "./sale-point.util";

/**
 * Thin wrapper over the generated sale-point client: adds the request headers
 * and turns an ApiResponse whose `status` is not "200" into an error, so
 * callers only deal with one failure path. The global error toast is skipped:
 * every caller shows its own translated message (or stays quiet when the
 * sale-point list is only optional context, e.g. on the orders pages).
 */
@Injectable({ providedIn: "root" })
export class SalePointService {
  constructor(
    private salePointApi: SalePointApiService,
    private orderApi: OrderApiService,
    private companyApi: CompanyApiService,
    private courier: CourierService,
    private mapsLinks: MapsLinkService,
  ) {}

  /** The main sale point being created: the panel and the rings screen may ask at the same time. */
  private creatingMain$: Observable<SalePointDto | null> | null = null;
  /** The store's position being read from the company's link; each link is tried once per visit (a bad link stays bad). */
  private placingMain$: Observable<SalePointDto | null> | null = null;
  private placingTried = new Set<string>();

  private readonly silent = new HttpContext().set(SKIP_ERROR_ALERT, true);

  private headers() {
    return { "x-api-canal": "web", "x-api-requestId": uuidv4() };
  }

  private unwrap<T>(response: { status?: string; body?: T }): T {
    if (!isApiResponseOk(response) || response.body == null) throw response;
    return response.body;
  }

  list(includeArchived = false): Observable<SalePointDto[]> {
    return this.salePointApi
      .getSalePoints({ ...this.headers(), includeArchived }, this.silent)
      .pipe(
        map((response) => {
          if (!isApiResponseOk(response)) throw response;
          return sortSalePoints(response.body || []);
        }),
      );
  }

  /**
   * {@link list}, except that a restaurant with no sale point at all (archived ones count) gets its main one
   * created first, from the shop's name and address and the courier pickup pin when set. With one store nobody
   * picks it, but its address, pin and delivery rings are the restaurant's. Other shops: the plain list.
   * A company's only store, with no position, takes it from the company's Google Maps link: nobody is asked twice.
   */
  listWithMain(includeArchived = false): Observable<SalePointDto[]> {
    return this.list(true).pipe(
      switchMap((all) => {
        if (all.length) {
          return of(all);
        }
        return this.createMain().pipe(map((main) => (main ? [main] : [])));
      }),
      switchMap((all) => this.withMainPlaced(all)),
      map((all) => (includeArchived ? all : all.filter((salePoint) => !salePoint.archived))),
    );
  }

  private withMainPlaced(all: SalePointDto[]): Observable<SalePointDto[]> {
    // With several stores nothing says which one the company's link shows, so nothing is written.
    const live = all.filter((salePoint) => !salePoint.archived);
    const main = live.length === 1 ? live[0] : undefined;
    const reference = main?.reference;
    if (!main || !reference || main.pickupLat != null || main.pickupLng != null) {
      return of(all);
    }
    if (!this.placingMain$) {
      this.placingMain$ = this.placeFromCompanyLink(reference).pipe(
        finalize(() => (this.placingMain$ = null)),
        shareReplay(1),
      );
    }
    return this.placingMain$.pipe(
      map((placed) => all.map((salePoint) => (placed && salePoint.reference === placed.reference ? { ...salePoint, ...placed } : salePoint))),
    );
  }

  /** The company's Google Maps link, to suggest a position the merchant then confirms. Null when there is none. */
  companyMapsLink(): Observable<string | null> {
    return this.companyApi.getCompanyByCompanyId(this.headers(), this.silent).pipe(
      map((response) => response.body?.googleMapsUrl?.trim() || null),
      catchError(() => of(null)),
    );
  }

  private placeFromCompanyLink(reference: string): Observable<SalePointDto | null> {
    return this.companyApi.getCompanyByCompanyId(this.headers(), this.silent).pipe(
      switchMap((response) => {
        const company = response.body;
        const link = company?.googleMapsUrl?.trim();
        // Remembered per link, and only once one is read: a link added later still places the store.
        const attempt = `${reference}|${link}`;
        if (company?.activity !== "RESTAURANT" || !link || this.placingTried.has(attempt)) {
          return of(null);
        }
        this.placingTried.add(attempt);
        return this.mapsLinks.coordinatesOf(link);
      }),
      switchMap((point) => {
        if (!point) {
          return of(null);
        }
        // Read again: reading the link takes seconds, and the merchant may have edited the store meanwhile.
        return this.list(true).pipe(
          switchMap((stores) => {
            const store = stores.find((salePoint) => salePoint.reference === reference);
            if (!store || store.pickupLat != null || store.pickupLng != null) {
              return of(null);
            }
            const { menuCustomized: _readOnly, ...body } = store;
            return this.update(reference, { ...body, pickupLat: point.lat, pickupLng: point.lng });
          }),
        );
      }),
      // Not allowed to edit stores, link unreadable, network: the merchant places the pin by hand as before.
      catchError(() => of(null)),
    );
  }

  private createMain(): Observable<SalePointDto | null> {
    if (!this.creatingMain$) {
      this.creatingMain$ = forkJoin({
        company: this.companyApi.getCompanyByCompanyId(this.headers(), this.silent).pipe(
          map((response) => response.body),
          catchError(() => of(undefined)),
        ),
        pickup: this.courier.getPickup().pipe(
          map((response) => response?.body ?? null),
          catchError(() => of(null)),
        ),
      }).pipe(
        switchMap(({ company, pickup }) => {
          const main = mainSalePointOf(company, pickup);
          return main ? this.create(main).pipe(catchError(() => of(null))) : of(null);
        }),
        finalize(() => (this.creatingMain$ = null)),
        shareReplay(1),
      );
    }
    return this.creatingMain$;
  }

  create(salePoint: SalePointDto): Observable<SalePointDto> {
    return this.salePointApi
      .createSalePoint({ ...this.headers(), body: salePoint }, this.silent)
      .pipe(map((response) => this.unwrap(response)));
  }

  update(reference: string, salePoint: SalePointDto): Observable<SalePointDto> {
    return this.salePointApi
      .updateSalePoint({ ...this.headers(), reference, body: salePoint }, this.silent)
      .pipe(map((response) => this.unwrap(response)));
  }

  archive(reference: string, archived: boolean): Observable<SalePointDto> {
    return this.salePointApi
      .archiveSalePoint({ ...this.headers(), reference, archived }, this.silent)
      .pipe(map((response) => this.unwrap(response)));
  }

  getMenu(reference: string): Observable<SalePointMenuDto> {
    return this.salePointApi
      .getSalePointMenu({ ...this.headers(), reference }, this.silent)
      .pipe(map((response) => this.unwrap(response)));
  }

  saveMenu(
    reference: string,
    menu: SalePointMenuDto,
  ): Observable<SalePointMenuDto> {
    return this.salePointApi
      .updateSalePointMenu({ ...this.headers(), reference, body: menu }, this.silent)
      .pipe(map((response) => this.unwrap(response)));
  }

  /** `salePointReference: null` removes the sale point from the order. */
  setOrderSalePoint(
    orderReference: string,
    salePointReference: string | null,
  ): Observable<Order> {
    return this.orderApi
      .updateOrderSalePoint({
        ...this.headers(),
        reference: orderReference,
        body: { salePointReference },
      }, this.silent)
      .pipe(map((response) => this.unwrap(response)));
  }
}
